import { supabase } from '../lib/supabase';

export interface Company {
  id: string;
  name: string;
  cnpj: string;
  legal_name?: string;
  address?: string;
  phone?: string;
  email?: string;
  is_active: boolean;
  subscription_plan: string;
  subscription_status: string;
  created_at: string;
  updated_at: string;
}

export interface UserCompanyMembership {
  id: string;
  user_id: string;
  company_id: string;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  company?: Company;
}

class CompanyService {
  /**
   * Get all companies user belongs to
   */
  async getUserCompanies(): Promise<{ data: Company[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('user_company_memberships')
        .select('company:companies(*)')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const companies = data?.map((m: any) => m.company).filter(Boolean) || [];
      return { data: companies, error: null };
    } catch (error) {
      console.error('Error fetching user companies:', error);
      return { data: null, error };
    }
  }

  /**
   * Get company details by ID
   */
  async getCompanyById(companyId: string): Promise<{ data: Company | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching company:', error);
      return { data: null, error };
    }
  }

  /**
   * Get user's role in a company
   */
  async getUserRoleInCompany(companyId: string): Promise<{ data: string | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('user_company_memberships')
        .select('role')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return { data: data?.role || null, error: null };
    } catch (error) {
      console.error('Error fetching user role:', error);
      return { data: null, error };
    }
  }

  /**
   * Get company members
   */
  async getCompanyMembers(companyId: string): Promise<{ data: any[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('user_company_memberships')
        .select(`
          *,
          user:user_profiles(id, email, full_name, avatar_url)
        `)
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching company members:', error);
      return { data: null, error };
    }
  }

  /**
   * Add user to company
   */
  async addUserToCompany(
    userId: string,
    companyId: string,
    role: string = 'member'
  ): Promise<{ data: any; error: any }> {
    try {
      const { data, error } = await supabase
        .from('user_company_memberships')
        .insert({
          user_id: userId,
          company_id: companyId,
          role: role,
          is_active: true
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error adding user to company:', error);
      return { data: null, error };
    }
  }

  /**
   * Update user role in company
   */
  async updateUserRole(
    membershipId: string,
    role: string
  ): Promise<{ data: any; error: any }> {
    try {
      const { data, error } = await supabase
        .from('user_company_memberships')
        .update({ role })
        .eq('id', membershipId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating user role:', error);
      return { data: null, error };
    }
  }

  /**
   * Remove user from company
   */
  async removeUserFromCompany(membershipId: string): Promise<{ error: any }> {
    try {
      const { error } = await supabase
        .from('user_company_memberships')
        .update({ is_active: false })
        .eq('id', membershipId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error removing user from company:', error);
      return { error };
    }
  }

  /**
   * Create new company
   */
  async createCompany(companyData: {
    name: string;
    cnpj: string;
    legal_name?: string;
    address?: string;
    phone?: string;
    email?: string;
  }): Promise<{ data: Company | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .insert(companyData)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating company:', error);
      return { data: null, error };
    }
  }

  /**
   * Update company
   */
  async updateCompany(
    companyId: string,
    updates: Partial<Company>
  ): Promise<{ data: Company | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .update(updates)
        .eq('id', companyId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating company:', error);
      return { data: null, error };
    }
  }
}

export const companyService = new CompanyService();